/* eslint-disable import/namespace */
import { hostURL } from '@/utils/functions/url-utils'
import * as Sentry from '@sentry/nextjs'
import {
  type PlaceOrderMutation,
  type PlaceOrderMutationVariables,
} from '../generated/platform-graphql'

import { useCart } from '@/components/cart/cart-context'
import { errorCodeMap } from '@/components/order/order-placed/error-page'
import { useUserActionError } from '@/components/utility/user-error/user-action-error-context'
import { isNullOrEmpty } from '@/utils/string-extensions'
import { useRouter } from 'next/navigation'
import { useState } from 'react'
import { routeCalculatorRelative } from '../route-calculator'
import { getPostcodeClient } from './postcode-client'

export const useCreateOrderPlatformClient = (isOneTouch: boolean) => {
  const [state, setState] = useState<
    boolean | 'error' | 'critical-error-not-paid' | 'critical-error-paid'
  >(false)
  const { clearCart, uiCart, lockUpsertCart } = useCart()
  const orderTotal = uiCart?.totalQuantity ?? 1
  const { setUserActionError } = useUserActionError()
  const router = useRouter()
  // eslint-disable-next-line sonarjs/cognitive-complexity
  const createOrder = async (placeOrder: PlaceOrderMutationVariables) => {
    lockUpsertCart(true)
    setState(true)
    try {
      const resp = await fetch(`${hostURL()}/api/place-order-platform`, {
        method: 'POST',
        body: JSON.stringify(placeOrder),
      })

      const response = (await resp.json()) as PlaceOrderMutation

      const orderResponse = response.createOrder

      if (orderResponse.errors !== null) {
        if (!isOneTouch && orderResponse.errors?.deleteCart === true) {
          clearCart()
        }

        const errorCode = errorCodeMap(
          orderResponse.errors?.message,
          orderResponse.errors?.userPaid,
        )

        if (isNullOrEmpty(orderResponse.errors?.permalink)) {
          router.replace(routeCalculatorRelative.orderError(errorCode))
        } else {
          clearCart()
          router.replace(
            routeCalculatorRelative.orderPlacedRoute(
              orderResponse.errors?.permalink,
              errorCode,
            ),
          )
        }

        setState('error')

        Sentry.captureException(
          orderResponse?.errors?.message ?? 'checkout-error',
          {
            extra: {
              postcode: getPostcodeClient(),
              cartTotal: uiCart?.total,
              cartItems: uiCart?.totalQuantity,
              cartId: uiCart?.id,
              averageCartValue:
                (uiCart?.total ?? 0) / (orderTotal > 0 ? orderTotal : 1),
              placeOrder,
              ...orderResponse.errors,
            },
          },
        )

        return orderResponse
      } else if (orderResponse.userActionError?.error_title != null) {
        if (
          !isOneTouch &&
          orderResponse.userActionError?.delete_cart === true
        ) {
          clearCart()
        }
        setUserActionError(orderResponse.userActionError)
        setState('error')

        Sentry.captureException(
          orderResponse?.userActionError?.error_message ?? 'checkout-error',
          {
            extra: {
              postcode: getPostcodeClient(),
              cartTotal: uiCart?.total,
              cartItems: uiCart?.totalQuantity,
              cartId: uiCart?.id,
              averageCartValue:
                (uiCart?.total ?? 0) / (orderTotal > 0 ? orderTotal : 1),
              placeOrder,
            },
          },
        )
        return orderResponse
      } else if (orderResponse.order !== null) {
        clearCart()

        router.replace(
          routeCalculatorRelative.orderPlacedRoute(
            response.createOrder.order?.permalink,
          ),
        )
        return response.createOrder
      }
    } catch (error) {
      // eslint-disable-next-line import/namespace
      Sentry.captureException(error)
      setState('error')
    } finally {
      lockUpsertCart(false)
    }
  }

  return {
    state,
    createOrder,
  }
}
